import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import './GroupManagement.css';
import API_URL from '../config';

const GroupManagement = () => {
  const { token } = useAuth();
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [loading, setLoading] = useState(true);
  const [membersLoading, setMembersLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Fetch all groups
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const res = await fetch(`${API_URL}/groups`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        // Filter out default Okta groups
        const abcGroups = data.filter(g =>
          !['Everyone', 'Okta Administrators'].includes(g.name)
        );
        setGroups(abcGroups);
      } catch (err) {
        console.error('Failed to fetch groups:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchGroups();
  }, [token]);

  // Fetch all Okta users for add member dropdown
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await fetch(`${API_URL}/okta/users`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        setAllUsers(data.filter(u => u.status === 'ACTIVE'));
      } catch (err) {
        console.error('Failed to fetch users:', err);
      }
    };
    fetchUsers();
  }, [token]);

  // Fetch group members
  const fetchMembers = useCallback(async (groupId) => {
    setMembersLoading(true);
    setMessage('');
    setError('');
    try {
      const res = await fetch(`${API_URL}/groups/${groupId}/members`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setMembers(data);
    } catch (err) {
      console.error('Failed to fetch members:', err);
    } finally {
      setMembersLoading(false);
    }
  }, [token]);

  const handleSelectGroup = (group) => {
    setSelectedGroup(group);
    setSelectedUserId('');
    fetchMembers(group.id);
  };

  const handleAddMember = async () => {
    if (!selectedUserId) return;
    setMessage('');
    setError('');

    try {
      const res = await fetch(`${API_URL}/groups/${selectedGroup.id}/members`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ userId: selectedUserId })
      });
      const data = await res.json();
      if (!res.ok) { setError(data.message); return; }
      setMessage(data.message);
      setSelectedUserId('');
      fetchMembers(selectedGroup.id);
    } catch (err) {
      setError('Failed to add member');
    }
  };

  const handleRemoveMember = async (userId) => {
    setMessage('');
    setError('');

    try {
      const res = await fetch(`${API_URL}/groups/${selectedGroup.id}/members/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) { setError(data.message); return; }
      setMessage(data.message);
      fetchMembers(selectedGroup.id);
    } catch (err) {
      setError('Failed to remove member');
    }
  };

  return (
    <Layout>
      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#003366' }}>Group Management</h1>
      </div>

      {message && <div className="success-message">{message}</div>}
      {error && <div className="error-message">{error}</div>}

      <div className="groups-layout">
        {/* Groups Sidebar */}
        <div className="groups-sidebar">
          <h2>ABC Corp Groups</h2>
          {loading ? (
            <p style={{ padding: '16px', fontSize: '14px', color: '#999' }}>Loading...</p>
          ) : (
            groups.map(group => (
              <div
                key={group.id}
                className={`group-item ${selectedGroup?.id === group.id ? 'active' : ''}`}
                onClick={() => handleSelectGroup(group)}
              >
                <div className="group-item-name">{group.name}</div>
                <div className="group-item-desc">{group.description || 'No description'}</div>
              </div>
            ))
          )}
        </div>

        {/* Group Detail */}
        <div className="group-detail">
          {!selectedGroup ? (
            <div className="no-selection">
              ← Select a group to view its members
            </div>
          ) : (
            <>
              <div className="group-detail-header">
                <h2>{selectedGroup.name}</h2>
                <span className="member-count">{members.length} members</span>
              </div>

              {membersLoading ? (
                <p style={{ fontSize: '14px', color: '#999' }}>Loading members...</p>
              ) : members.length === 0 ? (
                <div className="empty-state">No members in this group yet.</div>
              ) : (
                <table className="members-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Full Name</th>
                      <th>Email</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((member, index) => (
                      <tr key={member.id}>
                        <td>{index + 1}</td>
                        <td>{member.firstName} {member.lastName}</td>
                        <td>{member.email}</td>
                        <td>{member.status}</td>
                        <td>
                          <button
                            className="btn-remove"
                            onClick={() => handleRemoveMember(member.id)}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <div className="add-member-section">
                <select
                  value={selectedUserId}
                  onChange={e => setSelectedUserId(e.target.value)}
                >
                  <option value="">-- Add a user to this group --</option>
                  {allUsers
                    .filter(u => !members.find(m => m.id === u.id))
                    .map(u => (
                      <option key={u.id} value={u.id}>
                        {u.firstName} {u.lastName} ({u.email})
                      </option>
                    ))
                  }
                </select>
                <button
                  className="btn-primary"
                  onClick={handleAddMember}
                  disabled={!selectedUserId}
                >
                  Add Member
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default GroupManagement;